import { playAttentionTone } from '../attentionTone';
import type { SpeakOpts, SpeakResult, TtsProvider } from '../TtsProvider';

/**
 * The end of the chain, and an incident rather than a fallback.
 *
 * Reaching this means everything else failed: no cached audio, no network or no cloud permission,
 * no Piper, and no gesture for Web Speech. The person has typed a sentence and the device cannot
 * say it. Two things must then happen, and neither is optional:
 *
 *  1. **The text goes on screen at maximum size** — that is the partner view, so the person can
 *     still be understood by someone in the room. Being read is worse than being heard and far
 *     better than being silent.
 *  2. **One short tone**, so a total failure is never silent. Without it the app looks like it is
 *     working and the person waits for a response that will never come.
 *
 * It always "succeeds", because there is nothing after it. That makes the diagnostics loud on
 * purpose: `VisualOnly` serving an utterance is the signal to look at why.
 */
export function createVisualOnlyProvider(showPartnerView: (text: string) => void): TtsProvider {
  return {
    id: 'visualOnly',

    isAvailable(): boolean {
      // Never unavailable. A chain that can run out of providers is a chain that can leave a
      // person with nothing at all.
      return true;
    },

    async speak(text: string, _opts: SpeakOpts): Promise<SpeakResult> {
      const startedAtMs = performance.now();

      // Metadata only — the text itself is not logged (NFR7). The character count is enough to
      // tell a stuck-on-one-word failure from a whole-sentence one.
      console.error('[speech] VISUAL ONLY — every provider failed', { chars: text.length });

      showPartnerView(text);
      void playAttentionTone();

      return { providerId: 'visualOnly', startedAtMs, cached: false };
    },
  };
}
